import React from "react"
import { Link, StaticQuery, graphql } from "gatsby"
import { Helmet } from "react-helmet"
import Layout from "../components/Layout"
import Seo from "../components/Seo"
import { getPostImage } from "../data/blog-images"
import { getPlatformBySlug } from "../data/platforms"
import { formatBlogDate } from "../utils/formatBlogDate"

const BlogPostTemplate = ({ pageContext }) => {
  const { post, allPosts = [] } = pageContext
  const imageSrc = getPostImage(post) || post?.image
  const relatedPosts = (post.relatedPosts || [])
    .map(slug => allPosts.find(item => item.slug === slug))
    .filter(Boolean)
    .slice(0, 2)

  if (!post) {
    return (
      <Layout>
        <div className="container py-5">
          <h1>Post Not Found</h1>
          <Link to="/blog/">← Back to Blog</Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout bodyClass={`single-post post-${post.id}`}>
      <Seo
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        keywords={post.keywords || []}
        image={imageSrc || post.image}
        article={true}
        author={post.author}
        publishedTime={post.date}
        url={`/blog/${post.slug}/`}
      />
      <StaticQuery
        query={graphql`
          query BlogPostSiteUrl {
            site {
              siteMetadata {
                siteUrl
              }
            }
          }
        `}
        render={(data) => {
          const siteUrl = data.site.siteMetadata.siteUrl || ""
          const postUrl = `${siteUrl}/blog/${post.slug}`
          const postImage = imageSrc
            ? (imageSrc.startsWith("http") ? imageSrc : `${siteUrl}${imageSrc}`)
            : undefined

          const jsonLd = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.seoDescription || post.excerpt,
            url: postUrl,
            datePublished: post.date,
            dateModified: post.date,
            author: {
              "@type": "Person",
              name: post.author,
            },
            publisher: {
              "@type": "Organization",
              name: "Upfeed",
              url: siteUrl,
            },
            ...(postImage && { image: postImage }),
          }

          return (
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(jsonLd)}
              </script>
            </Helmet>
          )
        }}
      />
      
      <article className="container py-5">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-4">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/blog/">Blog</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {post.title}
                </li>
              </ol>
            </nav>

            {/* Article Header */}
            <header className="mb-4">
              <div className="mb-3">
                <span className="badge bg-primary me-2">{post.category}</span>
                <time dateTime={formatBlogDate(post.date)} className="text-muted">
                  {formatBlogDate(post.date)}
                </time>
                <span className="text-muted ms-2">• {post.readingTime}</span>
              </div>
              <h1 className="display-4 mb-3">{post.title}</h1>
              <div className="text-muted mb-4">
                <span>By {post.author}</span>
              </div>
            </header>

            {/* Featured Image */}
            {imageSrc && (
              <div className="mb-4">
                <img src={imageSrc} alt={post.title} className="img-fluid rounded" style={{ width: "100%", height: "auto" }} />
              </div>
            )}

            {/* Article Content */}
            <div
              className="article-content mb-5"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {post.relatedGuides && post.relatedGuides.length > 0 && (
              <section className="related-guides mb-5">
                <h2>Related setup guides</h2>
                <ul>
                  {post.relatedGuides.map(slug => {
                    const platform = getPlatformBySlug(slug)
                    if (!platform) return null

                    return (
                      <li key={slug}>
                        <Link to={`/add-product-feed/${platform.slug}/`}>
                          Adding your feed to {platform.name}
                        </Link>
                        {" — "}
                        {platform.desc}
                      </li>
                    )
                  })}
                </ul>
              </section>
            )}

            {relatedPosts.length > 0 && (
              <section className="related-articles mb-5">
                <h2>Related blog articles</h2>
                <ul>
                  {relatedPosts.map(relatedPost => (
                    <li key={relatedPost.slug}>
                      <Link to={`/blog/${relatedPost.slug}/`}>
                        {relatedPost.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Navigation */}
            <nav aria-label="Blog post navigation" className="border-top pt-4">
              <div className="d-flex justify-content-between">
                <Link to="/blog/" className="btn btn-outline-primary">
                  ← Back to Blog
                </Link>
                <Link to="/contact/" className="btn btn-primary">
                  Get in Touch
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </article>
    </Layout>
  )
}

export default BlogPostTemplate

